import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ExportButtons } from '@/components/ExportButtons';
import { DateFilter } from '@/components/DateFilter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { RefreshCw, Loader2, Beef, Ruler, Pill, CakeSlice, Cpu, AlertTriangle, Clock, TrendingUp, ArrowRight, Package, Bell, LayoutGrid } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import { Link } from 'react-router-dom';
import api from '@/lib/api';

const URGENCY_COLORS = { critical: 'bg-error/20 text-error border-error/30', medium: 'bg-warning/20 text-warning border-warning/30', low: 'bg-info/20 text-info border-info/30' };

export default function POSIndustryDashboardPage() {
  const [data, setData] = useState(null);
  const [dateFilter, setDateFilter] = useState({ start: null, end: null, period: "all" });
  const [loading, setLoading] = useState(true);
  const [alertResult, setAlertResult] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/pos/industry/dashboard');
      setData(res.data);
    } catch { toast.error('Failed to load dashboard'); } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const generateAlerts = async () => {
    try {
      const res = await api.get('/pos/industry/alerts/generate');
      setAlertResult(res.data);
      toast.success(`${res.data.new_notifications_created} new alerts created`);
    } catch { toast.error('Failed to generate alerts'); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" /></div></DashboardLayout>;
  if (!data) return <DashboardLayout><div className="text-center py-12 text-muted-foreground">No data available</div></DashboardLayout>;

  const { meat, tailoring, pharmacy, bakery, electronics } = data;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="pos-industry-dashboard">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-200/50">
              <LayoutGrid size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">Industry Overview</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Unified view across all POS industry types</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={generateAlerts} className="rounded-xl" data-testid="generate-alerts-btn"><Bell size={14} className="mr-1" />Generate Alerts</Button>
            <Button variant="outline" size="sm" onClick={fetchData} className="rounded-xl" data-testid="refresh-btn"><RefreshCw size={14} className="mr-1" />Refresh</Button>
          </div>
        </div>

        {/* Top Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="summary-cards">
          <SummaryCard icon={Beef} label="Meat Stock" value={`${meat.total_stock_kg} kg`} sub={`${meat.total_items} cuts`} alert={meat.low_stock_count > 0 ? `${meat.low_stock_count} low` : null} color="text-error" />
          <SummaryCard icon={Ruler} label="Tailoring" value={`${tailoring.active_orders} active`} sub={`${tailoring.total_orders} total`} alert={tailoring.overdue_orders > 0 ? `${tailoring.overdue_orders} overdue` : null} color="text-purple-600" />
          <SummaryCard icon={Pill} label="Pharmacy" value={`${pharmacy.total_items} items`} sub="in stock" alert={pharmacy.expiring_soon > 0 ? `${pharmacy.expiring_soon} expiring` : null} color="text-success" />
          <SummaryCard icon={CakeSlice} label="Bakery Today" value={`${bakery.produced_today} produced`} sub={`${bakery.sold_today} sold`} alert={bakery.remaining_today > 0 ? `${bakery.remaining_today} left` : null} color="text-warning" />
          <SummaryCard icon={Cpu} label="Electronics" value={`${electronics.total_inventory} items`} sub={`${electronics.active_repairs} repairs`} alert={electronics.warranty_expiring > 0 ? `${electronics.warranty_expiring} warranty` : null} color="text-info" />
        </div>

        {/* Alert Generation Result */}
        {alertResult && (
          <Card className="border-warning/30 bg-warning/10" data-testid="alert-result">
            <CardContent className="pt-3 pb-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold flex items-center gap-2"><Bell size={14} className="text-warning" />{alertResult.total_alerts} alerts found, {alertResult.new_notifications_created} new notifications</span>
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setAlertResult(null)}>Dismiss</Button>
              </div>
              <div className="flex gap-3 text-xs">
                {Object.entries(alertResult.alerts_by_category).map(([cat, count]) => count > 0 && (
                  <Badge key={cat} variant="outline" className="capitalize">{cat}: {count}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alert Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Meat Low Stock */}
          <Card className="border-error/30" data-testid="meat-alerts">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><Beef size={16} className="text-error" />Meat Low Stock Alerts</CardTitle>
                <Link to="/meat-cuts"><Button variant="ghost" size="sm" className="h-7 text-xs" data-testid="goto-meat">View All<ArrowRight size={12} className="ml-1" /></Button></Link>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {meat.low_stock_items.length === 0 ? (
                <p className="text-xs text-muted-foreground py-3">All stock levels are healthy</p>
              ) : (
                <div className="space-y-2">
                  {meat.low_stock_items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-error/10 border border-error/30">
                      <div>
                        <span className="text-sm font-medium">{item.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">{item.current_kg} / {item.min_kg} kg</span>
                      </div>
                      <Badge variant="destructive" className="text-[10px]">Reorder</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tailoring Overdue */}
          <Card className="border-purple-100" data-testid="tailoring-alerts">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><Ruler size={16} className="text-purple-500" />Tailoring Overdue Orders</CardTitle>
                <Link to="/tailoring-orders"><Button variant="ghost" size="sm" className="h-7 text-xs" data-testid="goto-tailoring">View All<ArrowRight size={12} className="ml-1" /></Button></Link>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {tailoring.overdue_list.length === 0 ? (
                <div className="flex items-center gap-2 py-3 text-xs text-success"><Clock size={14} />All orders are on schedule</div>
              ) : (
                <div className="space-y-2">
                  {tailoring.overdue_list.map((o, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-purple-50/50 border border-purple-100">
                      <div>
                        <span className="text-sm font-medium">{o.customer}</span>
                        <span className="text-xs text-muted-foreground ml-2">{o.garment}</span>
                      </div>
                      <Badge className="bg-error/20 text-error text-[10px] border-0">Due: {o.due}</Badge>
                    </div>
                  ))}
                </div>
              )}
              {tailoring.ready_for_pickup > 0 && (
                <div className="mt-2 p-2 rounded-lg bg-success/10 border border-success/30 text-xs text-success flex items-center gap-2">
                  <Package size={14} />{tailoring.ready_for_pickup} order{tailoring.ready_for_pickup > 1 ? 's' : ''} ready for pickup
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pharmacy Expiry */}
          <Card className="border-success/30" data-testid="pharmacy-alerts">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><Pill size={16} className="text-success" />Pharmacy Expiry Alerts</CardTitle>
                <Link to="/pharmacy-items"><Button variant="ghost" size="sm" className="h-7 text-xs" data-testid="goto-pharmacy">View All<ArrowRight size={12} className="ml-1" /></Button></Link>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {pharmacy.expiring_list.length === 0 && pharmacy.expired === 0 ? (
                <p className="text-xs text-muted-foreground py-3">No expiry concerns</p>
              ) : (
                <div className="space-y-2">
                  {pharmacy.expired > 0 && (
                    <div className="p-2 rounded-lg bg-error/10 border border-error/30 text-xs text-error flex items-center gap-2">
                      <AlertTriangle size={14} />{pharmacy.expired} item{pharmacy.expired > 1 ? 's' : ''} have expired
                    </div>
                  )}
                  {pharmacy.expiring_list.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-warning/10 border border-warning/30">
                      <div>
                        <span className="text-sm font-medium">{item.name}</span>
                        {item.batch && <span className="text-xs text-muted-foreground ml-2">Batch: {item.batch}</span>}
                      </div>
                      <Badge className="bg-warning/20 text-warning text-[10px] border-0">Exp: {item.expiry}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bakery Production */}
          <Card className="border-warning/30" data-testid="bakery-summary">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><CakeSlice size={16} className="text-warning" />Bakery Today</CardTitle>
                <Link to="/bakery-production"><Button variant="ghost" size="sm" className="h-7 text-xs" data-testid="goto-bakery">View All<ArrowRight size={12} className="ml-1" /></Button></Link>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {bakery.total_batches_today === 0 ? (
                <p className="text-xs text-muted-foreground py-3">No production batches today</p>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 rounded-lg bg-info/10 border border-info/30">
                      <div className="text-lg font-bold font-outfit text-info">{bakery.produced_today}</div>
                      <div className="text-[10px] text-muted-foreground">Produced</div>
                    </div>
                    <div className="p-2 rounded-lg bg-success/10 border border-success/30">
                      <div className="text-lg font-bold font-outfit text-success">{bakery.sold_today}</div>
                      <div className="text-[10px] text-muted-foreground">Sold</div>
                    </div>
                    <div className="p-2 rounded-lg bg-warning/10 border border-warning/30">
                      <div className="text-lg font-bold font-outfit text-warning">{bakery.remaining_today}</div>
                      <div className="text-[10px] text-muted-foreground">Remaining</div>
                    </div>
                  </div>
                  {bakery.produced_today > 0 && (
                    <div className="w-full bg-muted rounded-full h-2">
                      <div className="bg-success h-2 rounded-full transition-all" style={{ width: `${Math.min(100, (bakery.sold_today / bakery.produced_today) * 100)}%` }} />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Electronics */}
          <Card className="border-info/30 lg:col-span-2" data-testid="electronics-summary">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><Cpu size={16} className="text-info" />Electronics &amp; Repairs</CardTitle>
                <Link to="/electronics-warranty"><Button variant="ghost" size="sm" className="h-7 text-xs" data-testid="goto-electronics">View All<ArrowRight size={12} className="ml-1" /></Button></Link>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Inventory" value={electronics.total_inventory} icon={Package} />
                <Stat label="Active Repairs" value={electronics.active_repairs} icon={Cpu} color={electronics.active_repairs > 0 ? 'text-warning' : ''} />
                <Stat label="Ready for Pickup" value={electronics.repairs_ready} icon={Clock} color={electronics.repairs_ready > 0 ? 'text-success' : ''} />
                <Stat label="Warranty Expiring" value={electronics.warranty_expiring} icon={AlertTriangle} color={electronics.warranty_expiring > 0 ? 'text-error' : ''} />
              </div>
              {electronics.warranty_expiring_list.length > 0 && (
                <div className="mt-3 space-y-1">
                  {electronics.warranty_expiring_list.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-info/10 border border-info/30 text-xs">
                      <span className="font-medium">{item.product} {item.brand && `(${item.brand})`}</span>
                      <Badge className="bg-error/20 text-error text-[10px] border-0">Warranty ends: {item.warranty_end}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, alert, color }) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-2 mb-2">
          <Icon size={16} className={color || 'text-muted-foreground'} />
          <span className="text-xs text-muted-foreground font-medium">{label}</span>
        </div>
        <div className={`text-lg font-bold font-outfit ${color || ''}`}>{value}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
        {alert && <Badge className="mt-1 bg-warning/20 text-warning text-[10px] border-0">{alert}</Badge>}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, icon: Icon, color }) {
  return (
    <div className="text-center p-2 rounded-lg bg-muted/50 border">
      <Icon size={14} className={`mx-auto mb-1 ${color || 'text-muted-foreground'}`} />
      <div className={`text-xl font-bold font-outfit ${color || ''}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}
